import { ClientFactories, Store } from "bernie-core";
import { ClientLogger } from "bernie-client";
import { ShortlistStore } from "./ShortlistStore";
import { UIPageStore } from "./UIPageStore";
import { HotelsStore } from "./HotelsStore";
import { ThemeSelectorStore } from "./ThemeSelectorStore";
import { BexApiService } from "services/BexApiService";
import { ClientThumborStore } from "./thumbor/ClientThumborStore";
import { ClientStaticMapStore } from "./staticMap/ClientStaticMapStore";
import { DXStore } from "./DXStore";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { CookiesStore } from "stores/CookiesStore";
import { AdvertisingStore } from "./AdvertisingStore";
import { ClientMarkdownStore } from "./markdownHandler/ClientMarkdownStore";
import { ClientWizardStateStore } from "./wizard/ClientWizardStateStore";
import { alterClientStores } from "bernie-alter";
import { ContextInput } from "__generated__/api";
import { CompositionStore } from "src/stores/CompositionStore";
import { DestinationSuggestionsStore } from "stores/DestinationSuggestionsStore";
import { ClientFlexViewModelStore } from "stores/flexViewModel/ClientFlexViewModelStore";
import { ClientPoiStore } from "./poiStore/ClientPoiStore";
import { TCSService } from "services/TCSService";
import { TemplateStore } from "stores/TemplateStore";
import { RecentlyViewedStore } from "./RecentlyViewedStore";
import { MerchCampaignsStore } from "./MerchCampaignsStore";
import { ClientChatbotStore } from "./chatbotStore/ClientChatbotStore";
import { DealsStore } from "./DealsStore";
import { ClientMabStore } from "stores/mabStore/ClientMabStore";
import { PackagePillStore } from "./PackagePillStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { FlexModuleModelStore } from "./FlexModuleModelStore";
import { ClientRateCalendarStore } from "stores/rateCalendar";
import { CouponsStore } from "./CouponsStore";
import { HotwireConfigStore } from "./HotwireConfigStore";
import { PropertyFiltersStore } from "./PropertyFiltersStore";

interface WindowWithState extends Window {
  __STATE__: {
    context: ExtendedContextStore;
  };
}

export const buildBexApiServiceWithHeaders = (bexApiContext: ContextInput, logger: Logger) => {
  return new BexApiService("", logger, {
    "Device-User-Agent-ID": bexApiContext.identity.duaid,
    "x-page-id": ((window as unknown) as WindowWithState)?.__STATE__?.context?.pageId || null,
  });
};

export const stores: ClientFactories = {
  flexViewModel: (): Store =>
    new ClientFlexViewModelStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.viewmodel")),
  compositionStore: () => new CompositionStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.composition")),
  templateStore: (): Store => new TemplateStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.template")),
  flexModuleModelStore: () =>
    new FlexModuleModelStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.flexModuleModel")),
  shortlist: (): Store => new ShortlistStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.shortlist")),
  uiPage: (): Store => new UIPageStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.uiPage")),
  thumbor: (): Store => new ClientThumborStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.thumbor")),
  markdown: (): Store => new ClientMarkdownStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.markdown")),
  cookies: (): Store => new CookiesStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.cookies")),
  hotels: ({ bexApiContext }): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.hotels");
    const bexApiService = buildBexApiServiceWithHeaders(bexApiContext, logger);

    return new HotelsStore({}, logger, bexApiService);
  },
  recentlyViewed: ({ bexApiContext }): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.recentlyViewed");
    const bexApiService = buildBexApiServiceWithHeaders(bexApiContext, logger);

    return new RecentlyViewedStore({}, logger, bexApiService);
  },
  staticMap: (): Store => new ClientStaticMapStore({}, NOOP_LOGGER),
  dx: (): Store => new DXStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.dx")),
  advertising: (): AdvertisingStore =>
    new AdvertisingStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.advertising")),
  destinationSuggestions: (): Store =>
    new DestinationSuggestionsStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.destinationSuggestions")),
  wizardState: (): Store =>
    new ClientWizardStateStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.wizardState")),
  poi: (): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.poi");
    const tcsService = new TCSService("/blossomTcs");

    return new ClientPoiStore({}, tcsService, logger);
  },
  propertyFilters: (): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.propertyFilters");

    return new PropertyFiltersStore({}, logger);
  },
  merchCampaigns: ({ bexApiContext }): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.merchCampaigns");
    const bexApiService = buildBexApiServiceWithHeaders(bexApiContext, logger);

    return new MerchCampaignsStore({}, logger, bexApiService);
  },
  dealsStore: ({ bexApiContext }): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.dealsStore");
    const bexApiService = buildBexApiServiceWithHeaders(bexApiContext, logger);

    return new DealsStore({}, logger, bexApiService);
  },
  merchCoupons: ({ bexApiContext }): Store => {
    const logger = ClientLogger.getLoggerWithIdentifier("blossom.store.merchCoupons");
    const bexApiService = buildBexApiServiceWithHeaders(bexApiContext, logger);

    return new CouponsStore({}, logger, bexApiService);
  },

  ...alterClientStores,
  themeSelectorStore: () =>
    new ThemeSelectorStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.themeSelectorStore")),
  chatbot: (): Store => new ClientChatbotStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.chatbotStore")),
  mab: (): Store => new ClientMabStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.mabStore")),
  packagePillStore: () =>
    new PackagePillStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.packagePillStore")),
  rateCalendar: (): Store =>
    new ClientRateCalendarStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.rateCalendarStore")),
  hotwireConfig: (): Store =>
    new HotwireConfigStore({}, ClientLogger.getLoggerWithIdentifier("blossom.store.hotwireConfigStore")),
};
